<template>
  <component
    :is="isLink ? Link : 'button'"
    v-if="state"
    class="pointer-events-auto flex min-h-btn-md items-center gap-3 rounded-cart py-2 pl-3 pr-5 text-left shadow-float pj-tap hover:shadow-float-lg sm:flex-none"
    :class="pillClass"
    :href="isLink ? props.tabHref! : undefined"
    :type="isLink ? undefined : 'button'"
    @click="onClick"
  >
    <!-- Un-sent basket items count circle, and booking's ready party size -->
    <span
      v-if="state === 'order'"
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-item font-semibold"
    >
      {{ props.count }}
    </span>
    <span
      v-else-if="state === 'ready'"
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-item font-semibold"
    >
      {{ props.booking?.partySize }}
    </span>

    <!-- Icon badge for See Table Bill and Start Ordering, and booking's own
         unanswered steps — same treatment, a tint circle over a tonal bar. -->
    <span
      v-else-if="state === 'bill'"
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
    >
      <PjIcon
        :name="PJ_ICON.ORDERS"
        size="meta"
      />
    </span>
    <span
      v-else-if="state === 'start'"
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20"
    >
      <PjIcon
        :name="PJ_ICON.ORDERS"
        size="meta"
      />
    </span>
    <span
      v-else-if="stepIcon"
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
    >
      <PjIcon
        :name="stepIcon"
        size="meta"
      />
    </span>

    <span class="flex-1 text-title font-semibold sm:flex-none">
      {{ label }}
    </span>

    <span
      v-if="state === 'order'"
      class="ml-auto text-title font-semibold sm:ml-8"
    >
      {{ props.total }}
    </span>
    <span
      v-else-if="state === 'bill' && props.tabTotal"
      class="pj-num ml-auto text-title font-semibold text-primary sm:ml-6"
    >
      {{ props.tabTotal }}
    </span>
    <span
      v-else-if="state === 'ready'"
      class="ml-auto text-title font-semibold sm:ml-8"
    >
      {{ props.booking?.verb }}
    </span>
  </component>
</template>

<script lang="ts" setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'

import type { PjIconName } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import type { PjCartBarBookingStep } from '@/domains/pjazza/components/organisms/pj-cart-bar/PjCartBar.types.ts'

import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'

/**
 * The order, or the way into one — never empty while the venue is orderable.
 * On the booking page it doubles as the dock: same pill, same position, same
 * entry animation, holding the diner's running answer instead of a basket.
 * `booking` is the only prop that mode reads; no order prop below changes
 * meaning because of it.
 *
 * Four states, in priority order: something to send, a round already in the
 * kitchen, a seated tab with nothing on it yet, or — where the slot used to
 * render nothing at all — an invitation to start. That last state is why
 * `hasMenu` exists: an orderable venue with no sections yet has nowhere for a
 * tap to land, so it falls back to rendering nothing, exactly as before.
 * Booking's own four states are the same idea — one unanswered question at a
 * time, then ready — and take over only once none of the order states apply.
 *
 * Positioning is not this component's job — see `PjFloatingActions`, which
 * places this alongside the reserve control and owns the mount/leave
 * animation. This only ever renders the pill itself.
 */
const props = defineProps<{
  /** Absent in booking mode, where the item count means nothing. */
  count?: number
  /** Absent in booking mode. */
  total?: string
  /** Set once a round is with the kitchen. */
  sent?: boolean
  etaMinutes?: number | null
  /** True when the diner has an active table tab. */
  hasOpenTab?: boolean
  /** Running table tab total. */
  tabTotal?: string | null
  /** Link to Your Table page. */
  tabHref?: string | null
  /** False for a venue whose menu has no sections to jump to yet. */
  hasMenu?: boolean
  /** The booking dock. Absent on the menu page, where the order states apply instead. */
  booking?: {
    step: PjCartBarBookingStep
    /** Only read once `step` is `'ready'`. */
    partySize?: number | null
    /** "Sat 15 · 19:30" — only read once `step` is `'ready'`. */
    summaryLabel?: string
    /** "Book" / "Request a table" — only read once `step` is `'ready'`. */
    verb?: string
  }
}>()

const emit = defineEmits<{ open: []; start: []; 'booking-tap': [PjCartBarBookingStep] }>()

const STEP_LABEL: Record<Exclude<PjCartBarBookingStep, 'ready'>, string> = {
  guests: 'How many of you?',
  day: 'Which day?',
  time: 'Pick a time',
  name: 'Your name?'
}

const STEP_ICON: Record<Exclude<PjCartBarBookingStep, 'ready'>, PjIconName> = {
  guests: PJ_ICON.GUESTS,
  day: PJ_ICON.CALENDAR,
  time: PJ_ICON.CLOCK,
  name: PJ_ICON.YOU
}

const sentLabel = computed(() =>
  props.etaMinutes ? `In the kitchen · about ${props.etaMinutes} min` : 'In the kitchen'
)

const state = computed(() => {
  if ((props.count ?? 0) > 0 && !props.sent) return 'order'
  if (props.sent) return 'sent'
  if (props.hasOpenTab && props.tabHref) return 'bill'
  if (props.hasMenu) return 'start'
  if (props.booking) return props.booking.step

  return null
})

const isPrimary = computed(() => state.value === 'order' || state.value === 'start' || state.value === 'ready')

const isBookingStep = (value: typeof state.value): value is Exclude<PjCartBarBookingStep, 'ready'> =>
  value === 'guests' || value === 'day' || value === 'time' || value === 'name'

const stepIcon = computed(() => (isBookingStep(state.value) ? STEP_ICON[state.value] : null))

/** A pill that carries a running order spans the phone; an invitation keeps to its label. */
const isStretched = computed(() => state.value === 'order' || state.value === 'sent' || state.value === 'bill')

const pillClass = computed(() => [
  isPrimary.value
    ? 'bg-primary text-on-primary hover:bg-primary-hover'
    : 'bg-container text-on-container hover:bg-surface-press',
  { 'flex-1': isStretched.value }
])

const isLink = computed(() => state.value === 'bill')

const label = computed(() => {
  if (state.value === 'order') return 'Your order'
  if (state.value === 'sent') return sentLabel.value
  if (state.value === 'bill') return 'See table bill'
  if (state.value === 'ready') return props.booking?.summaryLabel ?? ''
  if (isBookingStep(state.value)) return STEP_LABEL[state.value]

  return 'Start ordering'
})

const onClick = (): void => {
  if (state.value === 'order' || state.value === 'sent') emit('open')
  else if (state.value === 'start') emit('start')
  else if (state.value === 'ready' || isBookingStep(state.value))
    emit('booking-tap', state.value as PjCartBarBookingStep)
}
</script>
